import React from 'react';
import {
    Document,
    Page,
    View,
    Text,
    StyleSheet,
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
    page: {
        padding: 15,
        fontSize: 10,
        fontFamily: 'Helvetica',
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 20,
        padding: 20
    },
    invoiceTitle: {
        fontSize: 20,
        fontweight: 'bold',
        marginBottom: 10,
    },
    customerName: {
        color: '#111827',
        marginLeft: 20,
        marginBottom: 3,
    },
    billToText: {
        color: '#374151',
        marginBottom: 5,
        lineHeight: 0.75,
        marginLeft: 20
    },
    meta: {
        textAlign: 'right',
        color: '#374151',
    },
    metaBlock: {
        display: 'flex',
        paddingLeft: 70,
    },
    metaLabel: {
        fontWeight: 'bold',
        fontSize: 10,
        color: '#374151',
    },
    metaValue: {
        fontSize: 10,
        color: '#374151',
        marginBottom: 3,
    },
    table: {
        marginBottom: 24,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        paddingVertical: 5,
        paddingHorizontal: 4,
        fontSize: 8
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f9fafb',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        paddingVertical: 4,
        paddingHorizontal: 4,
    },
    colItem: { width: '15%', paddingRight: 8 },
    colDesc: { width: '25%', paddingRight: 8 },
    colQty: { width: '10%', textAlign: 'right', paddingRight: 12 },
    colRate: { width: '15%', textAlign: 'right', paddingRight: 12 },
    colDisc: { width: '10%', textAlign: 'right', paddingRight: 12 },
    colGst: { width: '10%', textAlign: 'right', paddingRight: 10, paddingLeft: 12 },
    colAmount: { width: '15%', textAlign: 'right', paddingLeft: 10 },
    headerText: {
        fontWeight: 'bold',
        color: '#374151',
    },
    totalsWrap: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 16,
    },
    totalsBox: {
        marginTop: 30,
        width: 160,
    },
    totalsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 6,
    },
    totalsDivider: {
        borderTopWidth: 1,
        borderTopColor: '#e5e7eb',
        paddingTop: 8,
        marginTop: 4,
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontWeight: 'bold',
    },
    amountDueDivider: {
        borderTopWidth: 2,
        borderTopColor: '#1f2937',
        paddingTop: 8,
        marginTop: 4,
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontWeight: 'bold',
    },
    paymentRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    paymentLabel: {
        color: '#2563eb',
        fontSize: 9,
    },
    termsBox: {
        marginTop: 50,
        fontSize: 8,
        flexDirection: 'column',
    },
    termsText: {
        fontSize: 8,
        paddingBottom: 5
    },
});

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
}

function getTaxRatePercent(lineItem, taxes = []) {
    if (!lineItem?.tax_rate_id) return null;
    const tax = taxes.find((t) => t.tax_id === lineItem.tax_rate_id);
    if (tax) return parseFloat(tax.tax_rate);
    // Fallback: GST is commonly 10%
    if (lineItem.tax_rate_id === 'GST') return 10;
    return null;
}

/** Format rate for GST column: 10 → "10%", 10.5 → "10.5%" */
function formatGstRate(ratePercent) {
    if (ratePercent == null) return '—';
    return `${Number(ratePercent)}%`;
}

function getTotalGstLabel(formData, lineItems = [], taxes = []) {
    if (formData?.tax_inclusion === 'no_tax') return null;
    const firstWithTax = lineItems.find((li) => li.tax_rate_id);
    const rate = firstWithTax != null ? getTaxRatePercent(firstWithTax, taxes) : null;
    if (rate != null) return `TOTAL GST ${rate}%`;
    return 'TOTAL TAX';
}

export default function InvoicePdfDocument({ draftInvoice }) {
    if (!draftInvoice) return null;

    const { formData, lineItems = [], subtotal, taxTotal, grandTotal, taxes = [], company = {}, setup2, payments = [], totalPaid = 0, amountDue } = draftInvoice;
    const showGst = formData?.tax_inclusion !== 'no_tax';
    const totalGstLabel = showGst ? getTotalGstLabel(formData, lineItems, taxes) : null;
    const computedAmountDue = amountDue != null ? amountDue : Math.max((grandTotal || 0) - totalPaid, 0);
    const hasPayments = payments.length > 0;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.headerRow}>
                    <View style={styles.billTo}>
                        <Text style={styles.invoiceTitle}>TAX INVOICE</Text>
                        {formData.customer_name ? (
                            <Text style={styles.customerName}>{formData.customer_name}</Text>
                        ) : null}
                        <Text style={styles.billToText}>{formData.billing_address || '—'}</Text>
                    </View>
                    <View style={styles.meta}>
                        <View style={styles.metaBlock}>
                            <Text style={styles.metaLabel}>Invoice Number</Text>
                            <Text style={styles.metaValue}>{formData.invoice_number || '(Auto)'}</Text>
                        </View>
                        <View style={styles.metaBlock}>
                            <Text style={styles.metaLabel}>Invoice date</Text>
                            <Text style={styles.metaValue}>{formatDate(formData.invoice_date)}</Text>
                        </View>
                        <View style={styles.metaBlock}>
                            <Text style={styles.metaLabel}>Due date</Text>
                            <Text style={styles.metaValue}>{formatDate(formData.due_date)}</Text>
                        </View>
                        <View style={styles.metaBlock}>
                            <Text style={styles.metaLabel}>Terms</Text>
                            <Text style={styles.metaValue}>{formData.terms}</Text>
                        </View>
                        <View style={styles.metaBlock}>
                            <Text style={styles.metaLabel}>ABN</Text>
                            <Text style={styles.metaValue}>{company?.abnno || '—'}</Text>
                        </View>
                    </View>
                    <View>
                        <View style={[styles.metaBlock, { paddingLeft: 30, marginBottom: 2 }]}>
                        <Text style={styles.metaValue}>{company?.companyname || '—'}</Text>
                        <Text style={styles.metaValue}>{company?.street || '—'}</Text>
                        <Text style={styles.metaValue}>{[company?.city, company?.state, company?.postcode].filter(Boolean).join(' ') || '—'}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.table}>
                    <View fixed style={styles.tableHeader}>
                        <Text style={[styles.colItem, styles.headerText]}>Item</Text>
                        <Text style={[styles.colDesc, styles.headerText]}>Description</Text>
                        <Text style={[styles.colQty, styles.headerText]}>Quantity</Text>
                        <Text style={[styles.colRate, styles.headerText]}>Unit Price</Text>
                        <Text style={[styles.colDisc, styles.headerText]}>DISC</Text>
                        {showGst && <Text style={[styles.colGst, styles.headerText]}>GST</Text>}
                        <Text style={[styles.colAmount, styles.headerText]}>Amount AUD</Text>
                    </View>
                    {(lineItems || []).map((li, idx) => {
                        const ratePercent = getTaxRatePercent(li, taxes);
                        const gstDisplay = showGst ? formatGstRate(ratePercent) : null;

                        return (
                            <View key={li.id || idx} style={styles.tableRow}>
                                <Text style={styles.colItem}>{li.item_id || ''}</Text>
                                <Text style={styles.colDesc}>{li.item_description || ''}</Text>
                                <Text style={styles.colQty}>{li.quantity ?? ''}</Text>
                                <Text style={styles.colRate}>{Number(li.unit_price || 0).toFixed(2)}</Text>
                                <Text style={styles.colDisc}>{Number(li.line_discount_rate || 0).toFixed(2)}%</Text>
                                {showGst && <Text style={styles.colGst}>{gstDisplay}</Text>}
                                <Text style={styles.colAmount}>{Number(li.total_price || 0).toFixed(2)}</Text>
                            </View>
                        );
                    })}
                </View>

                <View style={styles.totalsWrap} wrap={false}>
                    <View style={styles.totalsBox}>
                        <View style={styles.totalsRow}>
                            <Text>Subtotal</Text>
                            <Text>{Number(subtotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                        </View>
                        {totalGstLabel != null && (
                            <View style={styles.totalsRow}>
                                <Text>{totalGstLabel}</Text>
                                <Text>{Number(taxTotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                            </View>
                        )}
                        <View style={styles.totalsDivider}>
                            <Text>TOTAL AUD</Text>
                            <Text>{Number(grandTotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                        </View>

                        {/* Total paid summary */}
                        {hasPayments && (
                            <View style={[styles.paymentRow, { marginTop: 8 }]}>
                                <Text style={styles.paymentLabel}>Less: Payments</Text>
                                <Text>{Number(totalPaid || 0).toFixed(2)}</Text>
                            </View>
                        )}

                        {hasPayments && (
                            <View style={styles.amountDueDivider}>
                                <Text style={{ fontWeight: 'bold', fontSize: 12 }}>Amount due</Text>
                                <Text style={{ fontWeight: 'bold', fontSize: 12 }}>{computedAmountDue.toFixed(2)}</Text>
                            </View>
                        )}
                    </View>
                </View>
                <View style={styles.termsBox} wrap={false}>
                <Text style={styles.termsText}>Terms and Conditions</Text>
                {/** Line break line in react-pdf */}
                <View style={{ borderBottomWidth: 1, borderBottomColor: 'black', marginVertical: 3 }} />
                    {/** Loop through setup2, for every row/function name, display the functionvalue */}
                    {(Array.isArray(setup2) ? setup2 : []).map((row, index) => (
                        <Text key={row.functionname ?? index} style={styles.termsText}>
                            {row.functionvalue}
                        </Text>
                    ))}
                </View>

                <Text
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        left: 15,
                        fontSize: 8,
                        color: '#6b7280',
                    }}
                    fixed
                    render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
                />
                <Text
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        fontSize: 8,
                        color: '#9ca3af',
                    }}
                    fixed
                >
                    Powered by runbusiness.io
                </Text>
            </Page>
        </Document>
    );
}
